import type {
	CourseOfferingClass,
	CourseOfferingProduct,
} from "@repo/utils/types/CourseOfferingTypes";
import {makeShopifyRequest} from "../../request";
import {castToMetaobject, getValue} from "../../operation-utils";
import type {
	GetCourseOfferingWithScheduleQuery,
	GetCourseOfferingWithScheduleQueryVariables,
} from "../../codegen/admin.generated";

export type {
	CourseOfferingClass,
	CourseOfferingClassInstances,
	CourseOfferingClassSchedule,
	CourseOfferingMeetingTime,
	CourseOfferingProduct,
} from "@repo/utils/types/CourseOfferingTypes";

/**
 * Max variants (class instances) returned for one course offering product.
 * Raise this (or add paging) if a single offering can exceed 100 class instances.
 */
const MAX_COURSE_OFFERING_VARIANTS = 100;

type RawVariant = NonNullable<
	NonNullable<
		GetCourseOfferingWithScheduleQuery["product"]
	>["variants"]["nodes"][number]
>;

/**
 * Map the Shopify variant to our CourseOfferingClass type.
 */
function mapClassInstance(variant: RawVariant): CourseOfferingClass | null {
	const classInstance = castToMetaobject(variant.classField);
	if (!classInstance) {
		return null;
	}

	const scheduleRaw = castToMetaobject(classInstance.scheduleField);
	const building = castToMetaobject(classInstance.campusBuildingField);
	const meetingNodes = scheduleRaw?.meetingsTimes?.references?.nodes ?? [];

	return {
		classId: getValue(classInstance.classIdField),
		instructorUserId: getValue(classInstance.instructorUserIdField),
		instructorName: null,
		registrationDeadlineAt: getValue(classInstance.registrationDeadlineAtField),
		schedule: scheduleRaw
			? {
					dateStartAt: getValue(scheduleRaw.dateStartAt),
					dateEndAt: getValue(scheduleRaw.dateEndAt),
					timezone: getValue(scheduleRaw.timezoneField),
					meetingTimes: meetingNodes.flatMap((node) => {
						if (!node || node.__typename !== "Metaobject") {
							return [];
						}
						return [
							{
								day: getValue(node.day),
								timeStartAt: getValue(node.timeStartAt),
								timeEndAt: getValue(node.timeEndAt),
							},
						];
					}),
				}
			: null,
		buildingId: getValue(building?.buildingIdField),
	};
}

/**
 * Map the Shopify product to our CourseOfferingProduct type.
 */
function mapCourseOfferingProduct(
	product: NonNullable<GetCourseOfferingWithScheduleQuery["product"]>
): CourseOfferingProduct {
	return {
		id: product.id,
		classInstances: product.variants.nodes.map((variant) => ({
			id: variant.id,
			seatsAvailable: variant.inventoryQuantity ?? 0,
			class: mapClassInstance(variant),
		})),
	};
}

/**
 * Loads a product and its variants. The product is a Course Offering
 * and the variants are the class instances for the schedule.
 *
 * Each variant includes remaining seats (`inventoryQuantity`) and flattened
 * class metadata (class id, instructor, registration deadline, nested
 * schedule + campus building).
 */
export async function getCourseOfferingWithSchedule(
	productId: string
): Promise<CourseOfferingProduct | null> {
	const variables: GetCourseOfferingWithScheduleQueryVariables = {
		id: productId,
		variantCount: MAX_COURSE_OFFERING_VARIANTS,
	};
	const response = await makeShopifyRequest<GetCourseOfferingWithScheduleQuery>(
		"admin",
		`#graphql
			query GetCourseOfferingWithSchedule($id: ID!, $variantCount: Int!) {
				product(id: $id) {
					id
					variants(first: $variantCount) {
						nodes {
							id
							inventoryQuantity
							classField: metafield(namespace: "custom", key: "class") {
								reference {
									__typename
									... on Metaobject {
										classIdField: field(key: "class_id") {
											value
										}
										instructorUserIdField: field(key: "instructor_user_id") {
											value
										}
										registrationDeadlineAtField: field(
											key: "registration_deadline_at"
										) {
											value
										}
										scheduleField: field(key: "schedule") {
											reference {
												__typename
												... on Metaobject {
													dateStartAt: field(key: "date_start_at") {
														value
													}
													dateEndAt: field(key: "date_end_at") {
														value
													}
													timezoneField: field(key: "timezone") {
														value
													}
													meetingsTimes: field(key: "meetings_times") {
														references(first: 20) {
															nodes {
																__typename
																... on Metaobject {
																	day: field(key: "day") {
																		value
																	}
																	timeStartAt: field(key: "time_start_at") {
																		value
																	}
																	timeEndAt: field(key: "time_end_at") {
																		value
																	}
																}
															}
														}
													}
												}
											}
										}
										campusBuildingField: field(key: "campus_building") {
											reference {
												__typename
												... on Metaobject {
													buildingIdField: field(key: "building_id") {
														value
													}
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		variables
	);

	if (!response.product) {
		return null;
	}

	// Convert the Shopify product to our CourseOfferingProduct type
	return mapCourseOfferingProduct(response.product);
}
